import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../core/select';
import { ExerciseType } from '@org/model';
import { cn } from '../../../../utils';
import type { ContentDisplayVariant } from '../../hooks/content-renderer-types';
import type { StudentExerciseContent } from '../../hooks/use-class-blocks';
import type {
  ExerciseFeedbackMessage,
  ExerciseSubmitHandler,
} from '../../hooks/content-renderer-types';
import { ContentDescription } from '../shared/content-description';
import { renderInlineMarkdown } from '../../../custom/markdown';
import { AnswerCorrection, answerFieldClassName, WorkbookPanel, ZineButton } from '../../zine';
import type { AnswerCheckState } from '../../zine';
import {
  splitWorkbookFeedback,
  WorkbookAttemptText,
  WorkbookExtraFeedback,
  useAnswerCorrectionLabels,
  WorkbookVerdict,
} from './zine-workbook';

interface SelectForBlankExerciseProps {
  content: StudentExerciseContent;
  onExerciseSubmit?: ExerciseSubmitHandler;
  feedback?: ExerciseFeedbackMessage[];
  variant?: ContentDisplayVariant;
  isReadOnly?: boolean;
}

const BLANK_MARKER = '___';

export function SelectForBlankExercise({
  content,
  onExerciseSubmit,
  feedback,
  variant = 'card',
  isReadOnly = false,
}: SelectForBlankExerciseProps) {
  void variant; // the workbook panel is the single presentation for this exercise
  const { t } = useTranslation('p2e');
  const correctionLabels = useAnswerCorrectionLabels();
  const sentences = content.sentences ?? [];
  const lastSubmission = useMemo(() => {
    const submission = content.lastSubmission;
    if (submission?.type === ExerciseType.SelectForBlank) {
      return submission;
    }
    return null;
  }, [content.lastSubmission]);
  const lastSelections = useMemo(() => {
    if (!lastSubmission) {
      return [];
    }
    return lastSubmission.selections ?? [];
  }, [lastSubmission]);
  const lastEvaluationDetails = useMemo(() => {
    const evaluation = (content.lastEvaluation as any) ?? null;
    const autoDetails = (evaluation?.auto as any)?.details ?? evaluation?.details;
    return autoDetails?.kind === 'SelectForBlank' ? autoDetails : null;
  }, [content.lastEvaluation]);
  const [selections, setSelections] = useState<number[][]>(() =>
    sentences.map((sentence, sentenceIndex) => {
      const blanks = sentence.blanks ?? [];
      const sentenceSelections = lastSelections[sentenceIndex] ?? [];
      return blanks.map((_, blankIndex) => {
        const value = sentenceSelections[blankIndex];
        return typeof value === 'number' && value >= 0 ? value : -1;
      });
    })
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [blankEvaluations, setBlankEvaluations] = useState<
    Array<
      Array<{
        isCorrect: boolean;
        correctLabel: string | null;
        grade?: 'perfect' | 'near' | 'partial' | 'wrong';
        reason?: string;
      }>
    >
  >([]);

  const blankSignature = useMemo(
    () => JSON.stringify(sentences.map((sentence) => (sentence.blanks ?? []).length)),
    [sentences]
  );

  useEffect(() => {
    setSelections(() =>
      sentences.map((sentence, sentenceIndex) => {
        const blanks = sentence.blanks ?? [];
        const sentenceSelections = lastSelections[sentenceIndex] ?? [];
        return blanks.map((_, blankIndex) => {
          const value = sentenceSelections[blankIndex];
          return typeof value === 'number' && value >= 0 ? value : -1;
        });
      })
    );
  }, [content.id, blankSignature, lastSelections]);

  const computeBlankEvaluations = useCallback(
    (submittedSelections: number[][]) =>
      sentences.map((sentence, sentenceIndex) => {
        const blanks = sentence.blanks ?? [];
        const sentenceSelections = submittedSelections[sentenceIndex] ?? [];

        return blanks.map((blank, blankIndex) => {
          const options = blank.options ?? [];
          const correctIndex = options.findIndex((option) => option.isCorrect);
          const correctLabel =
            correctIndex >= 0
              ? options[correctIndex]?.label ?? `Option ${correctIndex + 1}`
              : null;
          const submittedIndex = sentenceSelections[blankIndex];
          const isCorrect =
            typeof submittedIndex === 'number' &&
            submittedIndex >= 0 &&
            correctIndex >= 0 &&
            submittedIndex === correctIndex;
          const gradeFromBlank = (blank as any)?.grade as
            | 'perfect'
            | 'near'
            | 'partial'
            | 'wrong'
            | undefined;
          const reasonFromBlank = (blank as any)?.reason as string | undefined;

          return {
            isCorrect,
            correctLabel,
            grade: gradeFromBlank ?? (isCorrect ? 'perfect' : 'wrong'),
            reason: reasonFromBlank,
          };
        });
      }),
    [sentences],
  );

  const evaluationsFromPayload = useMemo(() => {
    if (!lastEvaluationDetails) {
      return null;
    }

    return (lastEvaluationDetails.sentences ?? []).map((sentence: any, sentenceIndex: number) => {
      const blanks = Array.isArray(sentence?.blanks) ? sentence.blanks : [];
      return blanks.map((blank: any, blankIndex: number) => {
        const correctOptionIndex = typeof blank?.correctOptionIndex === 'number' ? blank.correctOptionIndex : null;
        const contentOptions = sentences?.[sentenceIndex]?.blanks?.[blankIndex]?.options ?? [];
        const correctLabel =
          correctOptionIndex !== null
            ? contentOptions?.[correctOptionIndex]?.label ?? `Option ${correctOptionIndex + 1}`
            : null;

        return {
          isCorrect: Boolean(blank?.isCorrect),
          correctLabel,
          grade: blank?.grade as 'perfect' | 'near' | 'partial' | 'wrong' | undefined,
          reason: typeof blank?.reason === 'string' ? blank.reason : undefined,
        };
      });
    });
  }, [lastEvaluationDetails]);

  useEffect(() => {
    if (evaluationsFromPayload) {
      setBlankEvaluations(evaluationsFromPayload);
    } else if (lastSubmission) {
      setBlankEvaluations(computeBlankEvaluations(lastSubmission.selections ?? []));
    } else {
      setBlankEvaluations([]);
    }
  }, [computeBlankEvaluations, evaluationsFromPayload, lastSubmission]);

  const handleSelectionChange = (
    sentenceIndex: number,
    blankIndex: number,
    value: string
  ) => {
    if (isReadOnly) {
      return;
    }
    setSelections((previous) => {
      const next = previous.map((sentenceSelections) => [...sentenceSelections]);
      if (!next[sentenceIndex]) {
        next[sentenceIndex] = [];
      }
      next[sentenceIndex][blankIndex] = Number.parseInt(value, 10);
      return next;
    });
    setBlankEvaluations([]);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!onExerciseSubmit) {
      return;
    }

    try {
      setIsSubmitting(true);
      await onExerciseSubmit(content, {
        type: ExerciseType.SelectForBlank,
        selections,
      });
      // Evaluations update via useEffect when content.lastEvaluation changes after cache invalidation
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit = selections.length > 0;

  // "N of M correct" totals for the verdict box
  const selectionTotals = useMemo(() => {
    let total = 0;
    let correct = 0;
    blankEvaluations.forEach((sentenceEvaluations) => {
      sentenceEvaluations.forEach((evaluation) => {
        total += 1;
        if (evaluation.isCorrect) {
          correct += 1;
        }
      });
    });
    return { total, correct };
  }, [blankEvaluations]);

  const { verdictMessage, extraMessages } = splitWorkbookFeedback(feedback);
  const verdictOverride =
    verdictMessage?.source === 'auto' && selectionTotals.total > 0
      ? verdictMessage.variant === 'success'
        ? `${t('zine.workbook.correctCount', {
            correct: selectionTotals.correct,
            total: selectionTotals.total,
          })} — ${t('zine.workbook.perfect')}`
        : t('zine.workbook.correctCount', {
            correct: selectionTotals.correct,
            total: selectionTotals.total,
          })
      : undefined;

  const renderBlank = (
    blank: NonNullable<NonNullable<(typeof sentences)[number]['blanks']>[number]>,
    sentenceIndex: number,
    selectIndex: number,
    key: string,
  ) => {
    const options = blank?.options ?? [];
    const evaluation = blankEvaluations[sentenceIndex]?.[selectIndex] as
      | { isCorrect: boolean; correctLabel?: string | null; grade?: 'perfect' | 'near' | 'partial' | 'wrong'; reason?: string }
      | undefined;
    const grade = evaluation ? evaluation.grade ?? (evaluation.isCorrect ? 'perfect' : 'wrong') : null;
    const selectedIndex = selections[sentenceIndex]?.[selectIndex] ?? -1;
    const state: AnswerCheckState = !grade
      ? 'idle'
      : selectedIndex < 0
        ? 'empty'
        : grade === 'perfect' || grade === 'near'
          ? 'right'
          : 'wrong';
    const selectedLabel = selectedIndex >= 0 ? options[selectedIndex]?.label : undefined;
    const inlineKey = `${content.id}-select-correction-${sentenceIndex}-${selectIndex}`;

    return (
      <span key={key} className="mx-1 inline-flex items-center gap-1.5 align-middle">
        <Select
          value={
            selections[sentenceIndex]?.[selectIndex] !== undefined &&
            selections[sentenceIndex][selectIndex] >= 0
              ? String(selections[sentenceIndex][selectIndex])
              : undefined
          }
          onValueChange={(value) => handleSelectionChange(sentenceIndex, selectIndex, value)}
          disabled={options.length === 0 || isReadOnly}
        >
          <SelectTrigger
            className={cn(answerFieldClassName(state), 'w-auto min-w-[8.5rem] max-w-full gap-2 pr-2.5')}
          >
            <SelectValue placeholder={t('zine.workbook.selectPlaceholder')} />
          </SelectTrigger>
          <SelectContent>
            {options.map((option, optionIndex) => (
              <SelectItem
                key={option.optionId ?? `${sentenceIndex}-${selectIndex}-${optionIndex}`}
                value={String(optionIndex)}
              >
                {renderInlineMarkdown(
                  option.label || `Option ${optionIndex + 1}`,
                  `${content.id}-sentence-${sentenceIndex}-select-${selectIndex}-option-${optionIndex}`,
                )}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {state !== 'idle' && (
          <AnswerCorrection
            state={state}
            labels={correctionLabels}
            yourAnswer={selectedLabel ? renderInlineMarkdown(selectedLabel, `${inlineKey}-yours`) : undefined}
            correctAnswer={
              evaluation?.correctLabel
                ? renderInlineMarkdown(evaluation.correctLabel, `${inlineKey}-correct`)
                : undefined
            }
            reason={evaluation?.reason ? renderInlineMarkdown(evaluation.reason, `${inlineKey}-reason`) : undefined}
          />
        )}
      </span>
    );
  };

  return (
    <form onSubmit={handleSubmit}>
      <WorkbookPanel
        tab={t('zine.workbook.tab.autoChecked')}
        title={
          content.title ? renderInlineMarkdown(content.title, 'select-for-blank-title') : undefined
        }
        hint={t('zine.workbook.pickMissing')}
        footer={
          <>
            {onExerciseSubmit && !isReadOnly && (
              <ZineButton type="submit" isLoading={isSubmitting} disabled={!canSubmit || isSubmitting}>
                {isSubmitting ? t('zine.workbook.submitting') : t('zine.workbook.checkAnswers')}
              </ZineButton>
            )}
            {!isReadOnly && <WorkbookAttemptText content={content} />}
            <WorkbookVerdict pending={isSubmitting} message={verdictMessage} overrideText={verdictOverride} />
            <WorkbookExtraFeedback pending={isSubmitting} messages={extraMessages} />
          </>
        }
      >
        <ContentDescription text={content.description} markdown className="mb-3" />

        <div className="mb-4" style={{ lineHeight: 2.6, fontSize: '.95rem' }}>
          {sentences.map((sentence, sentenceIndex) => {
            const blanks = sentence.blanks ?? [];
            const parts = (sentence.sentence ?? '').split(BLANK_MARKER);

            return (
              <p
                key={sentence.sentenceId ?? `${content.id}-sentence-${sentenceIndex}`}
                className={cn('m-0 whitespace-pre-wrap', sentenceIndex > 0 && 'mt-2')}
              >
                {parts.map((part, partIndex) => (
                  <Fragment key={`${sentenceIndex}-part-${partIndex}`}>
                    {part &&
                      renderInlineMarkdown(
                        part,
                        `${content.id}-sentence-${sentenceIndex}-part-${partIndex}`,
                      )}
                    {partIndex < blanks.length &&
                      renderBlank(
                        blanks[partIndex]!,
                        sentenceIndex,
                        partIndex,
                        blanks[partIndex]?.blankId ?? `${sentenceIndex}-${partIndex}-${content.id}`,
                      )}
                  </Fragment>
                ))}
                {parts.length - 1 < blanks.length &&
                  blanks.slice(parts.length - 1).map((blank, blankIndex) => {
                    const selectIndex = (parts.length - 1) + blankIndex;
                    return renderBlank(
                      blank,
                      sentenceIndex,
                      selectIndex,
                      blank.blankId ?? `${sentenceIndex}-tail-${blankIndex}`,
                    );
                  })}
              </p>
            );
          })}
        </div>
      </WorkbookPanel>
    </form>
  );
}
